import { lazy, Suspense, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Select, SearchInput } from "@/components/inputs";
import { PageHead } from "@/components/PageHead";
import { useQuery } from "@tanstack/react-query";

import { assetData } from "@/api/client";
import { ContextStrip } from "@/modules/asset_data/ContextStrip";
import { DevicePicker } from "@/modules/asset_data/picker";
import { RecordTable } from "@/modules/asset_data/RecordTable";
import { TripList } from "@/modules/asset_data/TripList";
import { downloadCsv } from "@/lib/download";
import { localStamp, tzName } from "@/lib/format";

// lazy: Leaflet (~145KB) loads only when a History replay actually renders —
// every other page keeps the pre-map bundle size
const RouteReplay = lazy(() => import("@/modules/asset_data/RouteReplay").then((m) => ({ default: m.RouteReplay })));

/** value for <input type="datetime-local"> from a Date (local, minute precision). */
function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** THE DEFAULT WINDOW IS THE LAST 24 HOURS, everywhere on this page.
 *
 *  Dattatray: *"if use wants he can fetch the histry data with different time
 *  but the default should be be last 24 hours!"* — so this is a DEFAULT and
 *  not a fixed window: From, To and Resolution stay editable and Load re-runs
 *  whatever is in them. Arriving from the fleet map it also loads ITSELF,
 *  because nobody clicks History on a dot in order to then pick a date.
 *
 *  The policy lives HERE rather than in the link the map builds, so there is
 *  one place that decides what "history for this asset" means. */
const DEEP_HOURS = 24;
/** RAW. Dattatray: *"In histric data teh default resolution has to be Raw
 *  records"*, and it is a correctness fix rather than a preference.
 *
 *  BUCKETING AVERAGES `lat` AND `lng` (`_history_bucketed`: `avg(lat)`,
 *  `avg(lng)`). An averaged position is not a position the vehicle ever
 *  occupied -- across a 60 s bucket the mean of a turn sits INSIDE the
 *  corner, off the road -- so the route drawn from bucketed points is a
 *  smoothed approximation of the path, not the path. Averaging a voltage over
 *  a minute is sound; averaging a position is not the same operation wearing
 *  the same word.
 *
 *  WHAT THIS COSTS, stated rather than discovered: the cap is 5,000 records,
 *  so a 24 h window stays raw only while the device logs more slowly than
 *  about every 17 s. At the measured production cadence (p50 1 s / p90 30 s)
 *  a real day lands inside it; on the BENCH, where 1 Hz is a deliberate
 *  stress setting, 24 h is ~86,000 records and the window truncates to its
 *  first ~83 minutes. The truncation warning already says so and says to
 *  narrow the window, which is the correct move -- the previous default hid
 *  that by silently averaging instead.
 *
 *  Kept as a named constant because the deep link from the fleet map uses it
 *  too, so "history for this asset" means one thing in one place. */
const DEEP_BUCKET_S = 0;

export function AssetDataHistoryPage() {
  const [imei, setImei] = useState("");
  const now = new Date();
  const [from, setFrom] = useState(
    localInput(new Date(now.getTime() - DEEP_HOURS * 3600_000)));
  const [to, setTo] = useState(localInput(now));
  // Server-side downsampling: one averaged row per bucket instead of the raw
  // firehose. THE DEFAULT IS RAW -- see `DEEP_BUCKET_S`: bucketing averages
  // position, and an averaged position is not one the vehicle ever occupied.
  // Bucketing stays available for long windows, where the charts are the
  // point and the route is not.
  const [bucket, setBucket] = useState(DEEP_BUCKET_S);
  const [range, setRange] = useState<{ imei: string; from: string; to: string; bucket: number } | null>(null);

  // `?imei=` is how the fleet map hands a device over (it opens this page in a
  // new tab). The controls are set as well as the query, so the From/To/
  // Resolution boxes show the window that was actually loaded — a control
  // displaying a value its state does not hold is one of the bugs D-84 lists.
  const [params, setParams] = useSearchParams();
  const deep = params.get("imei");
  useEffect(() => {
    if (!deep) return;
    const t1 = new Date();
    const f = localInput(new Date(t1.getTime() - DEEP_HOURS * 3600_000));
    const t = localInput(t1);
    setImei(deep);
    setFrom(f);
    setTo(t);
    setBucket(DEEP_BUCKET_S);
    setRange({ imei: deep, from: f, to: t, bucket: DEEP_BUCKET_S });
    // consume the param, so a later pick from the picker is not fought by a
    // stale URL on the next render
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("imei"); return p; },
              { replace: true });
  }, [deep]);

  const q = useQuery({
    queryKey: ["live", "history", range?.imei, range?.from, range?.to, range?.bucket],
    queryFn: () => assetData.history(range!.imei, {
      from: new Date(range!.from).toISOString(),
      to: new Date(range!.to).toISOString(),
      bucket: range!.bucket ? String(range!.bucket) : undefined,
      limit: "5000",
    }),
    enabled: !!range,
  });
  const records = q.data?.records ?? [];
  const signals = q.data?.signals ?? [];

  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · History" title="Replay history"
        desc={<>Step back through a device's decoded records over any window (from the tel/his telemetry history) - same columns as the live table.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <DevicePicker imei={imei} onPick={setImei} />
        {imei && <div style={{ marginTop: 8 }}><ContextStrip imei={imei} /></div>}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
          <div>
            <label className="simlab">From</label>
            <SearchInput type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="simlab">To</label>
            <SearchInput type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <label className="simlab">Resolution</label>
            <Select style={{ minWidth: 130 }} value={bucket}
              onChange={(e) => setBucket(Number(e.target.value))}>
              <option value={0}>Raw records</option>
              <option value={60}>1 min avg</option>
              <option value={600}>10 min avg</option>
              <option value={3600}>1 hour avg</option>
            </Select>
          </div>
          <button className="btn teal" style={{ marginBottom: 1 }} disabled={!imei}
            onClick={() => setRange({ imei, from, to, bucket })}
            title={!imei ? "pick a device first" : ""}>Load</button>
          {range && (
            <span style={{ fontSize: 11.5, color: "var(--t-mute)", paddingBottom: 6 }}>
              {q.isFetching ? "loading…" : `${records.length} record${records.length === 1 ? "" : "s"}`}
            </span>
          )}
          {/* Exports WHAT IS ON SCREEN, which is the only honest thing it can
              claim: the query is capped at 5,000 rows and may be bucketed into
              averages, so a button promising "the data" would be lying about
              both. The filename carries the device and window so two
              downloads cannot be told apart only by their timestamps. */}
          {/* SAME COLOUR AS LOAD, because Dattatray asked for it and the
              reason holds: both are the primary action of this one bar: Load
              fetches the window, Download keeps it. `ghost` ranked it with
              Cancel. */}
          {range && records.length > 0 && !q.isFetching && (
            <button className="btn teal" style={{ marginBottom: 1 }}
              title="Download these rows as a spreadsheet (.csv, opens in Excel)"
              onClick={() => {
                // LOCAL TIME, LABELLED WITH THE ZONE. The API speaks UTC and
                // the console renders the browser's clock, so exporting `r.ts`
                // raw produced a file 5h30m off what the operator had just
                // watched. `localStamp` is the same conversion the table does
                // (both via lib/format, never a second one here); the zone
                // rides in the HEADER so a file read next week still says
                // which clock it is on.
                const tz = tzName();
                const tsKey = `ts (${tz})`;
                const rxKey = `received_at (${tz})`;
                downloadCsv(
                  `orbit-${range.imei}-${range.from}_${range.to}`
                    .replace(/[:\s]/g, "-") + ".csv",
                  // FLATTENED. The raw record nests every signal under
                  // `values`, so exporting it directly wrote the string
                  // "[object Object]" into one column and lost the entire
                  // payload -- the one thing the download exists for.
                  // `cursor` is an internal paging id and is dropped. Column
                  // order matches the table on screen.
                  records.map((r) => ({
                    [tsKey]: localStamp(r.ts),
                    seq: r.seq,
                    [rxKey]: r.received_at ? localStamp(r.received_at) : "",
                    ...r.values,
                  })),
                  [tsKey, "seq", rxKey]);
              }}>
              ⭳ Download
            </button>
          )}
        </div>
      </div>

      {range ? (
        <>
          {q.data?.truncated && (
            <div className="panel panel-pad" style={{ marginBottom: 14, borderLeft: "3px solid var(--warn)", color: "var(--t-mute)", fontSize: 12.5 }}>
              ⚠ This window holds more than 5,000 {range.bucket ? "buckets" : "records"} - showing the first 5,000.
              Narrow the time window{range.bucket ? "" : " or pick a coarser resolution"} to see everything.
            </div>
          )}
          {/* THE LIST BESIDE THE MAP, which is how WheelsEye lays it out
              and what Dattatray asked for: *"map and oute details UI can be
              clubbed together the way Wheels Eye does it for historic
              data"*. Reading a segment and finding it on the map is one
              glance rather than a scroll, and that pairing is the whole
              value of putting them on one screen.

              OURS IS FOR A CHOSEN RANGE, not a live truck. Theirs is a
              delivery/tracking product following a vehicle now; this page
              already owns From/To and a Load button, so the same layout
              answers "what happened to this asset between these times".

              THE LIST SCROLLS, THE MAP DOES NOT. Pinning the map means the
              thing you are looking things up ON stays put while you move
              down the list. The height is RouteReplay's own 340 px, matched
              here rather than guessed, so the two columns end level.

              IT STACKS BELOW 900 px, list first: at phone width a 380 px
              column beside a map gives two unusable halves, and the list is
              the half that still reads. */}
          {/* ONE TILE. The route details are drawn OVER the map rather
              than beside it, so full screen takes both -- they are the same
              element now, which is why nothing here has to coordinate two.

              The trip list takes THIS page's loaded window rather than
              growing a third date picker beside the two above it, and it
              fetches its own segments: they are computed server-side from
              every raw row, so they must not be derived from `records`,
              which the Resolution control may have bucketed down to one
              averaged row an hour. Deriving them here would make the trip
              list silently coarser as the window grew. */}
          {/* SAID WHEN IT APPLIES, because the control is two fields away
              and its effect on the MAP is not obvious from its name: "1 min
              avg" sounds like less detail, not like a different path. */}
          {range.bucket > 0 && (
            <div className="panel panel-pad" style={{ fontSize: 11.5,
                 color: "var(--warn)", marginBottom: 14 }}>
              Resolution is averaged, so the route below is <b>smoothed, not
              the path driven</b>: each point is the mean position over
              {" "}{range.bucket >= 60 ? `${range.bucket / 60} min` : `${range.bucket} s`},
              which cuts corners toward their inside. Choose
              {" "}<b>Raw records</b> to see where the vehicle actually went.
            </div>
          )}
          <Suspense fallback={<div className="panel panel-pad" style={{ height: 340, color: "var(--t-dim)", fontSize: 12, marginBottom: 14 }}>loading map…</div>}>
            <RouteReplay records={records} overlay={
              <TripList imei={range.imei} from={range.from} to={range.to} />
            } />
          </Suspense>
          {/* newest-first for reading; the replay above keeps its own
              oldest→newest order for the scrubber */}
          <RecordTable records={records} signals={signals} maxHeight={520} newestFirst />
        </>
      ) : (
        <div className="panel panel-pad cap">
          Pick a device, then press Load - the window defaults to the last
          {" "}{DEEP_HOURS} hours, and you can change it to anything.
          <div style={{ marginTop: 6, fontSize: 11.5 }}>
            Or click an asset on the Overview map: History opens in a new tab
            with those {DEEP_HOURS} hours already loaded.
          </div>
        </div>
      )}
    </div>
  );
}
